export type OptimizeImageOptions = {
  width?: number;
  height?: number;
  quality?: number;
};

const WESERV_ENDPOINT = 'https://images.weserv.nl/';

/**
 * Returns a resized/compressed display URL for list/card thumbnails.
 *
 * Media is served straight from S3 with no resize CDN of our own, so full
 * multi-MB originals were being downloaded for small grid tiles — the main
 * cause of slow image loads on /screens. This routes images through
 * images.weserv.nl (free, no API key, edge-cached) to fetch a right-sized,
 * compressed WebP instead. Falls back to the original URL if it can't be
 * parsed (e.g. relative/blob URLs) so nothing breaks.
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  options?: OptimizeImageOptions,
): string | undefined {
  if (!url) return undefined;

  const { width, height, quality = 75 } = options ?? {};

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return url;
    }

    const source = `${parsed.host}${parsed.pathname}${parsed.search}`;
    const params = new URLSearchParams();
    params.set('url', source);
    if (width) params.set('w', String(Math.round(width)));
    if (height) params.set('h', String(Math.round(height)));
    if (width && height) params.set('fit', 'cover');
    params.set('q', String(quality));
    params.set('output', 'webp');

    return `${WESERV_ENDPOINT}?${params.toString()}`;
  } catch {
    return url;
  }
}
